import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { patientsService, PatientCreate, PatientFilters, MedicalRecordCreate, VaccinationCreate, VaccinationUpdate, DewormingCreate, DewormingUpdate } from '@/services/patients';

interface UsePatientsOptions {
  enabled?: boolean;
}

export function usePatients(filters?: PatientFilters, options?: UsePatientsOptions) {
  const { enabled = true } = options || {};
  return useQuery({
    queryKey: ['patients', filters],
    queryFn: () => patientsService.getPatients(filters),
    enabled,
  });
}

export function usePatient(id: number | null) {
  return useQuery({
    queryKey: ['patient', id],
    queryFn: () => patientsService.getPatient(id!),
    enabled: !!id,
  });
}

export function useCreatePatient() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: PatientCreate) => patientsService.createPatient(data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['patients'] });
      queryClient.invalidateQueries({ queryKey: ['owners'] });
      queryClient.invalidateQueries({ queryKey: ['owner', variables.owner_id] });
      queryClient.invalidateQueries({ queryKey: ['owner-patients', variables.owner_id] });
    },
  });
}

export function useUpdatePatient() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<PatientCreate> }) =>
      patientsService.updatePatient(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['patients'] });
      queryClient.invalidateQueries({ queryKey: ['patient', variables.id] });
    },
  });
}

export function useDeletePatient() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => patientsService.deletePatient(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['patients'] });
    },
  });
}

// Medical Records
export function useMedicalRecords(patientId: number | null) {
  return useQuery({
    queryKey: ['medical-records', patientId],
    queryFn: () => patientsService.getMedicalRecords(patientId!),
    enabled: !!patientId,
  });
}

export function useCreateMedicalRecord() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ patientId, data }: { patientId: number; data: MedicalRecordCreate }) =>
      patientsService.createMedicalRecord(patientId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['medical-records', variables.patientId] });
      queryClient.invalidateQueries({ queryKey: ['patient', variables.patientId] });
    },
  });
}

export function useUpdateMedicalRecord() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ recordId, data }: { recordId: number; data: Partial<MedicalRecordCreate> }) =>
      patientsService.updateMedicalRecord(recordId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['medical-records'] });
    },
  });
}

// Attachments
export function useAttachments(recordId: number | null) {
  return useQuery({
    queryKey: ['attachments', recordId],
    queryFn: () => patientsService.getAttachments(recordId!),
    enabled: !!recordId,
  });
}

export function useUploadAttachment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      recordId,
      file,
      description,
      attachmentType,
    }: {
      recordId: number;
      file: File;
      description?: string;
      attachmentType?: string;
    }) => patientsService.uploadAttachment(recordId, file, description, attachmentType),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['attachments', variables.recordId] });
      queryClient.invalidateQueries({ queryKey: ['medical-records'] });
    },
  });
}

export function useDeleteAttachment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (attachmentId: number) => patientsService.deleteAttachment(attachmentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['attachments'] });
      queryClient.invalidateQueries({ queryKey: ['medical-records'] });
    },
  });
}

// Vacunación
export function useVaccinations(patientId: number | null) {
  return useQuery({
    queryKey: ['vaccinations', patientId],
    queryFn: () => patientsService.getVaccinations(patientId!),
    enabled: !!patientId,
  });
}

export function useCreateVaccination() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ patientId, data }: { patientId: number; data: VaccinationCreate }) =>
      patientsService.createVaccination(patientId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['vaccinations', variables.patientId] });
      queryClient.invalidateQueries({ queryKey: ['patient', variables.patientId] });
    },
  });
}

export function useUpdateVaccination() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      patientId,
      recordId,
      data,
    }: { patientId: number; recordId: number; data: VaccinationUpdate }) =>
      patientsService.updateVaccination(patientId, recordId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['vaccinations', variables.patientId] });
      queryClient.invalidateQueries({ queryKey: ['patient', variables.patientId] });
      // Las alertas de próxima dosis salen de estas fechas
      queryClient.invalidateQueries({ queryKey: ['alerts'] });
    },
  });
}

export function useDeleteVaccination() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ patientId, recordId }: { patientId: number; recordId: number }) =>
      patientsService.deleteVaccination(patientId, recordId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['vaccinations', variables.patientId] });
      queryClient.invalidateQueries({ queryKey: ['patient', variables.patientId] });
      queryClient.invalidateQueries({ queryKey: ['alerts'] });
    },
  });
}

// Desparasitación
export function useDewormings(patientId: number | null) {
  return useQuery({
    queryKey: ['dewormings', patientId],
    queryFn: () => patientsService.getDewormings(patientId!),
    enabled: !!patientId,
  });
}

export function useCreateDeworming() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ patientId, data }: { patientId: number; data: DewormingCreate }) =>
      patientsService.createDeworming(patientId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['dewormings', variables.patientId] });
      queryClient.invalidateQueries({ queryKey: ['patient', variables.patientId] });
    },
  });
}

export function useUpdateDeworming() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      patientId,
      recordId,
      data,
    }: { patientId: number; recordId: number; data: DewormingUpdate }) =>
      patientsService.updateDeworming(patientId, recordId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['dewormings', variables.patientId] });
      queryClient.invalidateQueries({ queryKey: ['patient', variables.patientId] });
      queryClient.invalidateQueries({ queryKey: ['alerts'] });
    },
  });
}

export function useDeleteDeworming() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ patientId, recordId }: { patientId: number; recordId: number }) =>
      patientsService.deleteDeworming(patientId, recordId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['dewormings', variables.patientId] });
      queryClient.invalidateQueries({ queryKey: ['patient', variables.patientId] });
      queryClient.invalidateQueries({ queryKey: ['alerts'] });
    },
  });
}
